import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import CountrySelector from '../../../components/CountrySelector';
import CurrencySelector from '../../../components/CurrencySelector';
import { useAppDispatch, useAppSelector } from '../../../store/hooks.ts';
import { useExcelTableState } from '../../../hooks/useExcelTableState.ts';
import {
  parsePasteData,
  calculateNextCell,
  applyPastedData,
  getRowBackgroundColor,
  getRowHoverColor,
} from '../../../utils/excelTableUtils.ts';
import {
  addArrangement,
  selectArrangementError,
  selectArrangementLoading,
  selectArrangements,
  setArrangementError,
  setArrangementLoading,
  setArrangements,
  removeArrangement,
  updateArrangement as updateArrangementState,
} from '../store/contractualArrangement/contractualArrangementSlice.ts';
import {
  addArrangementDetail,
  removeArrangementDetail,
  selectArrangementDetailError,
  selectArrangementDetailLoading,
  selectArrangementDetails,
  setArrangementDetailError,
  setArrangementDetailLoading,
  setArrangementDetails,
  updateArrangementDetail as updateArrangementDetailState,
} from '../store/contractualArrangementDetail/contractualArrangementDetailSlice.ts';
import {
  createDoraArrangement,
  createDoraArrangementDetail,
  deleteDoraArrangement,
  deleteDoraArrangementDetail,
  getICTServiceTypes,
  listDoraArrangementDetails,
  listDoraArrangements,
  updateDoraArrangement,
  updateDoraArrangementDetail,
} from '../../../api.ts';
import { DoraContractualArrangement } from '../models/doraContractualArrangement.ts';
import { DoraContractualArrangementDetail } from '../models/doraContractualArrangementDetail.ts';
import { selectCurrentOrganization } from '../../../store/organization/organizationSlice.ts';
import { ENTITY_NATURE_OPTIONS } from '../constants/entityNature.ts';

const ARRANGEMENT_TYPES = [
  'Overarching contractual arrangement',
  'Subsequent arrangement',
  'Associated arrangement',
  'Standalone arrangement',
];

const STATUSES = ['active', 'expired', 'terminated'];

const RELIANCE_LEVELS = ['Not significant', 'Low', 'Material', 'Full'];
const DATA_SENSITIVITY = ['Low', 'Medium', 'High'];

// Editable row interface for Excel-like arrangements table
interface EditableArrangementRow {
  id?: number;
  reference_number: string;
  arrangement_type: string;
  currency_code: string;
  annual_cost: number | '';
  overarching_reference: string;
  isNew?: boolean;
  touched?: boolean; // Track if user has edited this new row
}

const ContractualArrangementsPanel: React.FC = () => {
  const formatError = (err: any): string => {
    const detail = err?.response?.data?.detail ?? err?.message ?? err;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) return detail.map((d: any) => d?.msg || JSON.stringify(d)).join('; ');
    if (typeof detail === 'object') return JSON.stringify(detail);
    return 'Unexpected error';
  };

  const sanitizeDetailPayload = (
    form: DoraContractualArrangementDetail,
    overrideRef?: string | null,
  ): Omit<DoraContractualArrangementDetail, 'id' | 'organization_id'> => {
    const toNum = (v: any) =>
      v === undefined || v === null || v === '' || Number.isNaN(Number(v)) ? undefined : Number(v);

    return {
      ...form,
      arrangement_reference: overrideRef ?? form.arrangement_reference,
      entity_nature: form.entity_nature || null,
      branch_identification_code: form.branch_identification_code || null,
      notice_period_fe_days: toNum(form.notice_period_fe_days),
      notice_period_provider_days: toNum(form.notice_period_provider_days),
      start_date: form.start_date || null,
      end_date: form.end_date || null,
      termination_reason: form.termination_reason || null,
      governing_law_country: form.governing_law_country || null,
      service_provision_country: form.service_provision_country || null,
      data_location_rest: form.data_location_rest || null,
      data_location_processing: form.data_location_processing || null,
      data_sensitivity: form.data_sensitivity || null,
      reliance_level: form.reliance_level || null,
      storage_of_data:
        form.storage_of_data === undefined ? null : form.storage_of_data,
    };
  };

  const dispatch = useAppDispatch();
  const arrangements = useAppSelector(selectArrangements);
  const arrangementLoading = useAppSelector(selectArrangementLoading);
  const arrangementError = useAppSelector(selectArrangementError);

  const details = useAppSelector(selectArrangementDetails);
  const detailLoading = useAppSelector(selectArrangementDetailLoading);
  const detailError = useAppSelector(selectArrangementDetailError);

  const currentOrg = useAppSelector(selectCurrentOrganization);

  const [expandedArrangementIds, setExpandedArrangementIds] = useState<Set<number>>(new Set());
  const [expandedDetailIds, setExpandedDetailIds] = useState<Set<number>>(new Set());
  const [addingDetailForArrangementId, setAddingDetailForArrangementId] = useState<number | null>(null);
  const [ictServiceTypes, setIctServiceTypes] = useState<Array<{ code: string; description: string }>>([]);

  // Excel-like table state for arrangements
  const [editableArrangements, setEditableArrangements] = useState<EditableArrangementRow[]>([]);

  const {
    focusedRowIndex,
    editingCell,
    setEditingCell,
    handleRowFocus: baseHandleRowFocus,
    handleRowBlur: baseHandleRowBlur,
    isSaving,
    markSaving,
    markSaved,
  } = useExcelTableState<EditableArrangementRow>();

  // Arrangement dialog state
  const [arrangementDialogOpen, setArrangementDialogOpen] = useState(false);
  const [editingArrangement, setEditingArrangement] = useState<DoraContractualArrangement | null>(null);
  const [arrangementForm, setArrangementForm] = useState<DoraContractualArrangement>({
    reference_number: '',
    arrangement_type: '',
    currency_code: 'EUR',
    annual_cost: 0,
    overarching_reference: '',
  });

  // Detail dialog state
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [editingDetail, setEditingDetail] = useState<DoraContractualArrangementDetail | null>(null);
  const [detailForm, setDetailForm] = useState<DoraContractualArrangementDetail>({
    arrangement_reference: '',
    financial_entity_lei: '',
    entity_nature: '',
    branch_identification_code: '',
    provider_id_code: '',
    provider_id_type: '',
    function_identifier: '',
    ict_service_type: '',
    start_date: new Date().toISOString().split('T')[0],
    end_date: '9999-12-31',
    termination_reason: '',
    notice_period_fe_days: undefined,
    notice_period_provider_days: undefined,
    governing_law_country: '',
    service_provision_country: '',
    storage_of_data: undefined,
    data_location_rest: '',
    data_location_processing: '',
    data_sensitivity: '',
    reliance_level: '',
  });

  const canEdit = Boolean(currentOrg?.role && currentOrg.role !== 'viewer');

  // Field order for Tab navigation in arrangements table
  const fieldOrder: (keyof EditableArrangementRow)[] = [
    'reference_number',
    'arrangement_type',
    'overarching_reference',
    'currency_code',
    'annual_cost',
  ];

  // Helper to determine if a required field is empty
  const isRequiredFieldEmpty = (row: EditableArrangementRow, field: keyof EditableArrangementRow): boolean => {
    const requiredFields: (keyof EditableArrangementRow)[] = [
      'reference_number',
      'arrangement_type',
      'currency_code',
    ];
    if (!requiredFields.includes(field)) return false;

    const value = row[field];
    return value === '' || value === null || value === undefined;
  };

  // Helper to get cell background color with subtle amber shine for required empty fields
  const getCellBackgroundColor = (row: EditableArrangementRow, field: keyof EditableArrangementRow): string => {
    // Don't highlight untouched new rows
    if (row.isNew && !row.touched) {
      return 'transparent';
    }

    if (isRequiredFieldEmpty(row, field)) {
      // Brighter, more fancy amber/gold shine with subtle gradient effect
      return 'linear-gradient(135deg, rgba(255, 193, 7, 0.12), rgba(255, 160, 0, 0.08))';
    }
    return 'transparent';
  };

  const loadArrangements = useCallback(async () => {
    try {
      dispatch(setArrangementLoading(true));
      dispatch(setArrangementError(null));
      const data = await listDoraArrangements();
      dispatch(setArrangements(data));
    } catch (err: any) {
      dispatch(setArrangementError(formatError(err) || 'Failed to load arrangements'));
    } finally {
      dispatch(setArrangementLoading(false));
    }
  }, [dispatch]);

  const loadDetails = useCallback(async () => {
    try {
      dispatch(setArrangementDetailLoading(true));
      dispatch(setArrangementDetailError(null));
      // Load all details (no filter)
      const data = await listDoraArrangementDetails();
      dispatch(setArrangementDetails(data));
    } catch (err: any) {
      dispatch(setArrangementDetailError(formatError(err) || 'Failed to load details'));
    } finally {
      dispatch(setArrangementDetailLoading(false));
    }
  }, [dispatch]);

  useEffect(() => {
    loadArrangements();
  }, [loadArrangements]);

  useEffect(() => {
    loadDetails();
  }, [loadDetails]);

  useEffect(() => {
    const loadServiceTypes = async () => {
      try {
        const types = await getICTServiceTypes();
        setIctServiceTypes(types);
      } catch (err) {
        console.error('Failed to load ICT service types:', err);
      }
    };
    loadServiceTypes();
  }, []);

  const resetArrangementDialog = () => {
    setEditingArrangement(null);
    setArrangementForm({
      reference_number: '',
      arrangement_type: '',
      currency_code: 'EUR',
      annual_cost: 0,
      overarching_reference: '',
    });
  };

  const openArrangementDialog = (arr?: DoraContractualArrangement) => {
    if (arr) {
      setEditingArrangement(arr);
      setArrangementForm({
        ...arr,
        overarching_reference: arr.overarching_reference ?? '',
      });
    } else {
      resetArrangementDialog();
    }
    setArrangementDialogOpen(true);
  };

  const handleSaveArrangement = async () => {
    if (!arrangementForm.reference_number || !arrangementForm.arrangement_type || !arrangementForm.currency_code) {
      dispatch(setArrangementError('Reference, type, and currency are required'));
      return;
    }

    try {
      dispatch(setArrangementLoading(true));
      dispatch(setArrangementError(null));
      if (editingArrangement?.id) {
        const updated = await updateDoraArrangement(editingArrangement.id, {
          ...arrangementForm,
          overarching_reference: arrangementForm.overarching_reference || null,
        });
        dispatch(updateArrangementState(updated));
      } else {
        const created = await createDoraArrangement({
          ...arrangementForm,
          overarching_reference: arrangementForm.overarching_reference || null,
        });
        dispatch(addArrangement(created));
      }
      setArrangementDialogOpen(false);
      resetArrangementDialog();
    } catch (err: any) {
      dispatch(setArrangementError(formatError(err) || 'Failed to save arrangement'));
    } finally {
      dispatch(setArrangementLoading(false));
    }
  };

  const handleDeleteArrangement = async (arr: DoraContractualArrangement) => {
    if (!arr.id) return;
    if (!window.confirm(`Delete arrangement ${arr.reference_number}? This will also delete associated details.`)) return;
    try {
      dispatch(setArrangementLoading(true));
      dispatch(setArrangementError(null));
      await deleteDoraArrangement(arr.id);
      dispatch(removeArrangement(arr.id));
      // Clear expanded state
      setExpandedArrangementIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(arr.id!);
        return newSet;
      });
    } catch (err: any) {
      dispatch(setArrangementError(formatError(err) || 'Failed to delete arrangement'));
    } finally {
      dispatch(setArrangementLoading(false));
    }
  };

  const resetDetailDialog = () => {
    setEditingDetail(null);
    const arrangement = addingDetailForArrangementId
      ? arrangements.find(a => a.id === addingDetailForArrangementId)
      : null;
    setDetailForm({
      arrangement_reference: arrangement?.reference_number || '',
      financial_entity_lei: '',
      entity_nature: '',
      branch_identification_code: '',
      provider_id_code: '',
      provider_id_type: '',
      function_identifier: '',
      ict_service_type: '',
      start_date: new Date().toISOString().split('T')[0],
      end_date: '9999-12-31',
      termination_reason: '',
      notice_period_fe_days: undefined,
      notice_period_provider_days: undefined,
      governing_law_country: '',
      service_provision_country: '',
      storage_of_data: undefined,
      data_location_rest: '',
      data_location_processing: '',
      data_sensitivity: '',
      reliance_level: '',
    });
  };

  const openDetailDialog = (detail?: DoraContractualArrangementDetail, arrangementId?: number) => {
    if (detail) {
      setEditingDetail(detail);
      const arrangement = arrangements.find(a => a.reference_number === detail.arrangement_reference);
      setAddingDetailForArrangementId(arrangement?.id ?? null);
      setDetailForm({
        ...detail,
        arrangement_reference: detail.arrangement_reference || '',
        entity_nature: detail.entity_nature ?? '',
        branch_identification_code: detail.branch_identification_code ?? '',
        termination_reason: detail.termination_reason ?? '',
        governing_law_country: detail.governing_law_country ?? '',
        service_provision_country: detail.service_provision_country ?? '',
        data_location_rest: detail.data_location_rest ?? '',
        data_location_processing: detail.data_location_processing ?? '',
        data_sensitivity: detail.data_sensitivity ?? '',
        reliance_level: detail.reliance_level ?? '',
      });
    } else {
      setAddingDetailForArrangementId(arrangementId ?? null);
      resetDetailDialog();
    }
    setDetailDialogOpen(true);
  };

  const handleSaveDetail = async () => {
    const arrangement = addingDetailForArrangementId
      ? arrangements.find(a => a.id === addingDetailForArrangementId)
      : null;

    if (!arrangement?.reference_number) {
      dispatch(setArrangementDetailError('Select an arrangement first'));
      return;
    }
    if (
      !detailForm.arrangement_reference ||
      !detailForm.financial_entity_lei ||
      !detailForm.provider_id_code ||
      !detailForm.provider_id_type ||
      !detailForm.function_identifier ||
      !detailForm.ict_service_type
    ) {
      dispatch(setArrangementDetailError('All required fields must be filled'));
      return;
    }
    if (detailForm.financial_entity_lei.length !== 20) {
      dispatch(setArrangementDetailError('Financial Entity LEI must be exactly 20 characters'));
      return;
    }

    try {
      dispatch(setArrangementDetailLoading(true));
      dispatch(setArrangementDetailError(null));
      if (editingDetail?.id) {
        const updated = await updateDoraArrangementDetail(
          editingDetail.id,
          sanitizeDetailPayload(detailForm, arrangement.reference_number),
        );
        dispatch(updateArrangementDetailState(updated));
      } else {
        const created = await createDoraArrangementDetail(
          sanitizeDetailPayload(detailForm, arrangement.reference_number),
        );
        dispatch(addArrangementDetail(created));
      }
      setDetailDialogOpen(false);
      setAddingDetailForArrangementId(null);
      resetDetailDialog();
    } catch (err: any) {
      dispatch(setArrangementDetailError(formatError(err) || 'Failed to save detail'));
    } finally {
      dispatch(setArrangementDetailLoading(false));
    }
  };

  const handleDeleteDetail = async (detail: DoraContractualArrangementDetail) => {
    if (!detail.id) return;
    if (!window.confirm(`Delete detail for provider ${detail.provider_id_code}?`)) return;
    try {
      dispatch(setArrangementDetailLoading(true));
      dispatch(setArrangementDetailError(null));
      await deleteDoraArrangementDetail(detail.id);
      dispatch(removeArrangementDetail(detail.id));
    } catch (err: any) {
      dispatch(setArrangementDetailError(formatError(err) || 'Failed to delete detail'));
    } finally {
      dispatch(setArrangementDetailLoading(false));
    }
  };

  const toggleArrangementExpanded = (arrangementId: number) => {
    setExpandedArrangementIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(arrangementId)) {
        newSet.delete(arrangementId);
      } else {
        newSet.add(arrangementId);
      }
      return newSet;
    });
  };

  const toggleDetailExpanded = (detailId: number) => {
    setExpandedDetailIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(detailId)) {
        newSet.delete(detailId);
      } else {
        newSet.add(detailId);
      }
      return newSet;
    });
  };

  // Excel-like table handlers for arrangements
  const updateArrangementRow = (index: number, field: keyof EditableArrangementRow, value: any) => {
    setEditableArrangements(prev => {
      const updated = [...prev];
      const row = updated[index];
      // Mark new rows as touched when first edited
      updated[index] = {
        ...row,
        [field]: value,
        ...(row.isNew && !row.touched ? { touched: true } : {})
      };
      return updated;
    });
  };

  const handleArrangementRowFocus = (index: number) => {
    baseHandleRowFocus(index, editableArrangements, saveArrangementRow);
  };

  const handleArrangementRowBlur = () => {
    baseHandleRowBlur(editableArrangements, saveArrangementRow);
  };

  const handleArrangementTabNavigation = (
    e: React.KeyboardEvent,
    currentRow: number,
    currentField: keyof EditableArrangementRow
  ) => {
    if (e.key !== 'Tab') return;

    e.preventDefault();

    const currentFieldIndex = fieldOrder.indexOf(currentField);
    const nextCell = calculateNextCell(
      currentRow,
      currentFieldIndex,
      editableArrangements.length,
      fieldOrder.length,
      e.shiftKey
    );

    if (nextCell) {
      if (nextCell.row !== currentRow) {
        handleArrangementRowFocus(nextCell.row);
      }
      setEditingCell({ row: nextCell.row, field: fieldOrder[nextCell.fieldIndex] });
    }
  };

  const saveArrangementRow = useCallback(async (row: EditableArrangementRow, index: number) => {
    const rowKey = row.id ?? `new-${index}`;

    // Prevent duplicate saves
    if (isSaving(rowKey)) return;

    try {
      if (row.isNew) {
        // Create new arrangement only if required fields are filled
        if (row.reference_number.trim() && row.arrangement_type && row.currency_code) {
          markSaving(rowKey);
          const created = await createDoraArrangement({
            reference_number: row.reference_number.trim(),
            arrangement_type: row.arrangement_type,
            currency_code: row.currency_code,
            annual_cost: Number(row.annual_cost) || 0,
            overarching_reference: row.overarching_reference || null,
          });
          dispatch(addArrangement(created));
        }
      } else if (row.id) {
        // Update existing arrangement
        if (row.reference_number.trim() && row.arrangement_type && row.currency_code) {
          markSaving(rowKey);
          const updated = await updateDoraArrangement(row.id, {
            reference_number: row.reference_number.trim(),
            arrangement_type: row.arrangement_type,
            currency_code: row.currency_code,
            annual_cost: Number(row.annual_cost) || 0,
            overarching_reference: row.overarching_reference || null,
          });
          dispatch(updateArrangementState(updated));
        }
      }
    } catch (err: any) {
      console.error('Failed to save arrangement', err);
      dispatch(setArrangementError(err?.response?.data?.detail || 'Failed to save arrangement'));
    } finally {
      markSaved(rowKey);
    }
  }, [dispatch, isSaving, markSaving, markSaved]);

  const handleArrangementPaste = useCallback((
    e: React.ClipboardEvent,
    startRowIndex: number,
    startField: keyof EditableArrangementRow
  ) => {
    e.preventDefault();
    const pasteData = e.clipboardData.getData('text');
    const startFieldIndex = fieldOrder.indexOf(startField);

    // Parse the pasted data
    const parsedRows = parsePasteData(pasteData);

    // Normalize parsed data: treat visual "empty" representations as actual empty strings
    const normalizedRows = parsedRows.map(row =>
      row.map(cell => {
        const trimmed = cell.trim();
        // Treat various dash characters and common empty representations as empty
        if (trimmed === '—' || trimmed === '-' || trimmed === '–' || trimmed === '−' || trimmed === 'N/A' || trimmed === 'n/a') {
          return '';
        }
        return cell;
      })
    );

    // Apply pasted data to rows
    const createEmptyRow = (): EditableArrangementRow => ({
      reference_number: '',
      arrangement_type: '',
      currency_code: 'EUR',
      annual_cost: '',
      overarching_reference: '',
      isNew: true,
      touched: true, // Pasted rows are considered touched
    });

    setEditableArrangements(prev => {
      const updated = applyPastedData(
        prev,
        normalizedRows,
        startRowIndex,
        startFieldIndex,
        fieldOrder,
        new Map(),
        createEmptyRow
      );
      // Mark affected rows as touched
      return updated.map((row, idx) => {
        if (idx >= startRowIndex && idx < startRowIndex + normalizedRows.length && row.isNew) {
          return { ...row, touched: true };
        }
        return row;
      });
    });

    // Auto-save pasted rows after a short delay
    setTimeout(() => {
      parsedRows.forEach((_, rowOffset) => {
        const targetRowIndex = startRowIndex + rowOffset;
        if (targetRowIndex < editableArrangements.length) {
          saveArrangementRow(editableArrangements[targetRowIndex], targetRowIndex);
        }
      });
    }, 300);
  }, [editableArrangements, saveArrangementRow, fieldOrder]);

  // Group details by arrangement
  const detailsByArrangement = useMemo(() => {
    const grouped: { [key: number]: DoraContractualArrangementDetail[] } = {};
    details.forEach(detail => {
      const arr = arrangements.find(a => a.reference_number === detail.arrangement_reference);
      if (arr?.id) {
        if (!grouped[arr.id]) grouped[arr.id] = [];
        grouped[arr.id].push(detail);
      }
    });
    return grouped;
  }, [details, arrangements]);

  // Convert arrangements to editable rows with empty row at end
  const rows = useMemo(() => {
    const existingRows: EditableArrangementRow[] = arrangements.map(a => ({
      id: a.id,
      reference_number: a.reference_number,
      arrangement_type: a.arrangement_type,
      currency_code: a.currency_code,
      annual_cost: a.annual_cost,
      overarching_reference: a.overarching_reference || '',
    }));

    // Add empty row for new entries
    const emptyRow: EditableArrangementRow = {
      reference_number: '',
      arrangement_type: '',
      currency_code: 'EUR',
      annual_cost: '',
      overarching_reference: '',
      isNew: true,
    };

    return [...existingRows, emptyRow];
  }, [arrangements]);

  useEffect(() => {
    setEditableArrangements(rows);
  }, [rows]);

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Contractual Arrangements (B_02.01 & B_02.02)
      </Typography>

      {arrangementError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {arrangementError}
        </Alert>
      )}

      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="subtitle1">Arrangements</Typography>
        <Typography variant="caption" color="text.secondary">
          Click cells to edit • Tab to navigate • Changes save automatically
        </Typography>
      </Stack>

      <TableContainer component={Paper} sx={{ mb: 3, border: '1px solid', borderColor: 'divider' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell width={50} sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'primary.contrastText' }} />
              <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'primary.contrastText' }}>Reference</TableCell>
              <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'primary.contrastText' }}>Type</TableCell>
              <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'primary.contrastText' }}>Overarching</TableCell>
              <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'primary.contrastText' }}>Currency</TableCell>
              <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'primary.contrastText' }}>Annual Cost</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'primary.contrastText', width: 60 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {editableArrangements.map((row, index) => {
              const isFocused = focusedRowIndex === index;
              const isEvenRow = index % 2 === 0;
              const isExpanded = row.id ? expandedArrangementIds.has(row.id) : false;
              const arrDetails = row.id ? detailsByArrangement[row.id] || [] : [];

              return (
                <React.Fragment key={row.id ?? `new-${index}`}>
                  <TableRow
                    sx={{
                      bgcolor: getRowBackgroundColor(isFocused, row.isNew || false, isEvenRow),
                      '&:hover': {
                        bgcolor: getRowHoverColor(isFocused),
                      },
                      transition: 'background-color 0.15s ease',
                    }}
                  >
                    <TableCell>
                      {!row.isNew && (
                        <IconButton
                          size="small"
                          onClick={() => row.id && toggleArrangementExpanded(row.id)}
                          disabled={!row.id}
                        >
                          {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                        </IconButton>
                      )}
                    </TableCell>

                    {/* Reference Number Cell */}
                    <TableCell width="20%">
                      {editingCell?.row === index && editingCell?.field === 'reference_number' ? (
                        <TextField
                          value={row.reference_number}
                          onChange={(e) => updateArrangementRow(index, 'reference_number', e.target.value)}
                          onBlur={() => {
                            handleArrangementRowBlur();
                            setEditingCell(null);
                          }}
                          onKeyDown={(e) => handleArrangementTabNavigation(e, index, 'reference_number')}
                          onPaste={(e) => handleArrangementPaste(e, index, 'reference_number')}
                          size="small"
                          fullWidth
                          variant="standard"
                          placeholder={row.isNew ? 'Enter reference...' : ''}
                          InputProps={{ disableUnderline: true }}
                          sx={{ '& input': { p: 1 } }}
                          autoFocus
                          disabled={!row.isNew} // Reference can't be changed after creation
                        />
                      ) : (
                        <div
                          onClick={() => {
                            if (row.isNew && canEdit) {
                              handleArrangementRowFocus(index);
                              setEditingCell({ row: index, field: 'reference_number' });
                            }
                          }}
                          style={{
                            padding: '8px',
                            minHeight: '20px',
                            cursor: row.isNew && canEdit ? 'text' : 'default',
                            userSelect: 'text',
                            background: getCellBackgroundColor(row, 'reference_number'),
                            transition: 'background 0.15s ease',
                          }}
                        >
                          {row.reference_number || (row.isNew ? <span style={{ color: '#999' }}>Enter reference...</span> : '')}
                        </div>
                      )}
                    </TableCell>

                    {/* Arrangement Type Cell */}
                    <TableCell width="20%">
                      {editingCell?.row === index && editingCell?.field === 'arrangement_type' ? (
                        <FormControl fullWidth size="small" variant="standard">
                          <Select
                            value={row.arrangement_type || ''}
                            onChange={(e: SelectChangeEvent<string>) => {
                              updateArrangementRow(index, 'arrangement_type', e.target.value);
                            }}
                            onBlur={() => {
                              handleArrangementRowBlur();
                              setEditingCell(null);
                            }}
                            onKeyDown={(e) => handleArrangementTabNavigation(e, index, 'arrangement_type')}
                            displayEmpty
                            disableUnderline
                            sx={{ '& .MuiSelect-select': { p: 1 } }}
                            autoFocus
                          >
                            {row.isNew && <MenuItem value=""><em>Select type...</em></MenuItem>}
                            {ARRANGEMENT_TYPES.map(type => (
                              <MenuItem key={type} value={type}>{type}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      ) : (
                        <div
                          onClick={() => {
                            if (canEdit) {
                              handleArrangementRowFocus(index);
                              setEditingCell({ row: index, field: 'arrangement_type' });
                            }
                          }}
                          style={{
                            padding: '8px',
                            minHeight: '20px',
                            cursor: canEdit ? 'pointer' : 'default',
                            userSelect: 'text',
                            background: getCellBackgroundColor(row, 'arrangement_type'),
                            transition: 'background 0.15s ease',
                          }}
                        >
                          {row.arrangement_type || (row.isNew ? <span style={{ color: '#999' }}>Select type...</span> : '')}
                        </div>
                      )}
                    </TableCell>

                    {/* Overarching Reference Cell */}
                    <TableCell width="20%">
                      {editingCell?.row === index && editingCell?.field === 'overarching_reference' ? (
                        <Autocomplete
                          value={row.overarching_reference || null}
                          onChange={(_, newValue) => {
                            updateArrangementRow(index, 'overarching_reference', newValue || '');
                          }}
                          onBlur={() => {
                            handleArrangementRowBlur();
                            setEditingCell(null);
                          }}
                          onKeyDown={(e) => handleArrangementTabNavigation(e, index, 'overarching_reference')}
                          options={arrangements
                            .filter(a => a.id !== row.id && a.reference_number)
                            .map(a => a.reference_number)}
                          freeSolo
                          size="small"
                          fullWidth
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              variant="standard"
                              placeholder="Select or type..."
                              InputProps={{
                                ...params.InputProps,
                                disableUnderline: true,
                              }}
                              sx={{ '& input': { p: 1 } }}
                            />
                          )}
                          sx={{ '& .MuiAutocomplete-input': { p: '8px !important' } }}
                          autoFocus
                        />
                      ) : (
                        <div
                          onClick={() => {
                            if (canEdit) {
                              handleArrangementRowFocus(index);
                              setEditingCell({ row: index, field: 'overarching_reference' });
                            }
                          }}
                          style={{
                            padding: '8px',
                            minHeight: '20px',
                            cursor: canEdit ? 'pointer' : 'default',
                            userSelect: 'text',
                            background: getCellBackgroundColor(row, 'overarching_reference'),
                            transition: 'background 0.15s ease',
                          }}
                        >
                          {row.overarching_reference || '—'}
                        </div>
                      )}
                    </TableCell>

                    {/* Currency Code Cell */}
                    <TableCell width="15%">
                      {editingCell?.row === index && editingCell?.field === 'currency_code' ? (
                        <Box
                          onBlur={() => {
                            handleArrangementRowBlur();
                            setEditingCell(null);
                          }}
                          onKeyDown={(e) => handleArrangementTabNavigation(e, index, 'currency_code')}
                        >
                          <CurrencySelector
                            value={row.currency_code}
                            onChange={(code) => updateArrangementRow(index, 'currency_code', code)}
                            label=""
                            required
                            disabled={!canEdit}
                          />
                        </Box>
                      ) : (
                        <div
                          onClick={() => {
                            if (canEdit) {
                              handleArrangementRowFocus(index);
                              setEditingCell({ row: index, field: 'currency_code' });
                            }
                          }}
                          style={{
                            padding: '8px',
                            minHeight: '20px',
                            cursor: canEdit ? 'pointer' : 'default',
                            userSelect: 'text',
                            background: getCellBackgroundColor(row, 'currency_code'),
                            transition: 'background 0.15s ease',
                          }}
                        >
                          {row.currency_code || (row.isNew ? <span style={{ color: '#999' }}>EUR</span> : '')}
                        </div>
                      )}
                    </TableCell>

                    {/* Annual Cost Cell */}
                    <TableCell width="15%">
                      {editingCell?.row === index && editingCell?.field === 'annual_cost' ? (
                        <TextField
                          type="number"
                          value={row.annual_cost}
                          onChange={(e) => updateArrangementRow(index, 'annual_cost', e.target.value === '' ? '' : Number(e.target.value))}
                          onBlur={() => {
                            handleArrangementRowBlur();
                            setEditingCell(null);
                          }}
                          onKeyDown={(e) => handleArrangementTabNavigation(e, index, 'annual_cost')}
                          onPaste={(e) => handleArrangementPaste(e, index, 'annual_cost')}
                          size="small"
                          fullWidth
                          variant="standard"
                          placeholder="0"
                          InputProps={{ disableUnderline: true }}
                          sx={{ '& input': { p: 1 } }}
                          autoFocus
                        />
                      ) : (
                        <div
                          onClick={() => {
                            if (canEdit) {
                              handleArrangementRowFocus(index);
                              setEditingCell({ row: index, field: 'annual_cost' });
                            }
                          }}
                          style={{
                            padding: '8px',
                            minHeight: '20px',
                            cursor: canEdit ? 'text' : 'default',
                            userSelect: 'text',
                            background: getCellBackgroundColor(row, 'annual_cost'),
                            transition: 'background 0.15s ease',
                          }}
                        >
                          {row.annual_cost !== '' ? row.annual_cost : (row.isNew ? <span style={{ color: '#999' }}>0</span> : '0')}
                        </div>
                      )}
                    </TableCell>

                    {/* Actions Cell */}
                    <TableCell align="right">
                      {!row.isNew && (
                        <IconButton
                          onClick={() => handleDeleteArrangement(row as DoraContractualArrangement)}
                          size="small"
                          disabled={!canEdit}
                          sx={{ opacity: 0.6, '&:hover': { opacity: 1, color: 'error.main' } }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                  {/* Nested details section */}
                  <TableRow>
                    <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={10}>
                      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                        <Box sx={{ margin: 2 }}>
                          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                            <Typography variant="subtitle2">Details for {row.reference_number}</Typography>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<AddIcon />}
                              onClick={() => openDetailDialog(undefined, row.id)}
                              disabled={!canEdit || !row.id}
                            >
                              Add Detail
                            </Button>
                          </Stack>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell width={50} />
                                <TableCell>Financial Entity LEI</TableCell>
                                <TableCell>Provider Code</TableCell>
                                <TableCell>Function ID</TableCell>
                                <TableCell>ICT Service Type</TableCell>
                                <TableCell>Start</TableCell>
                                <TableCell>End</TableCell>
                                <TableCell align="right">Actions</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {arrDetails.map(detail => {
                                const isDetailExpanded = detail.id ? expandedDetailIds.has(detail.id) : false;
                                return (
                                  <React.Fragment key={detail.id ?? `${detail.arrangement_reference}-${detail.provider_id_code}`}>
                                    <TableRow>
                                      <TableCell>
                                        <IconButton
                                          size="small"
                                          onClick={() => detail.id && toggleDetailExpanded(detail.id)}
                                          disabled={!detail.id}
                                        >
                                          {isDetailExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                                        </IconButton>
                                      </TableCell>
                                      <TableCell>{detail.financial_entity_lei}</TableCell>
                                      <TableCell>{detail.provider_id_code}</TableCell>
                                      <TableCell>{detail.function_identifier}</TableCell>
                                      <TableCell>{detail.ict_service_type}</TableCell>
                                      <TableCell>{detail.start_date}</TableCell>
                                      <TableCell>{detail.end_date}</TableCell>
                                      <TableCell align="right">
                                        <Button
                                          size="small"
                                          startIcon={<EditIcon />}
                                          onClick={() => openDetailDialog(detail)}
                                          disabled={!canEdit}
                                        >
                                          Edit
                                        </Button>
                                        <Button
                                          size="small"
                                          color="error"
                                          startIcon={<DeleteIcon />}
                                          onClick={() => handleDeleteDetail(detail)}
                                          disabled={!canEdit}
                                        >
                                          Delete
                                        </Button>
                                      </TableCell>
                                    </TableRow>
                                    {/* Detail additional fields */}
                                    <TableRow>
                                      <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={8}>
                                        <Collapse in={isDetailExpanded} timeout="auto" unmountOnExit>
                                          <Box sx={{ margin: 2 }}>
                                            <Typography variant="caption" gutterBottom component="div">
                                              Additional Details
                                            </Typography>
                                            <Grid container spacing={2}>
                                              <Grid item xs={6} sm={4}>
                                                <Typography variant="caption" color="text.secondary">Entity Nature</Typography>
                                                <Typography variant="body2">{detail.entity_nature || '—'}</Typography>
                                              </Grid>
                                              <Grid item xs={6} sm={4}>
                                                <Typography variant="caption" color="text.secondary">Branch Code</Typography>
                                                <Typography variant="body2">{detail.branch_identification_code || '—'}</Typography>
                                              </Grid>
                                              <Grid item xs={6} sm={4}>
                                                <Typography variant="caption" color="text.secondary">Provider ID Type</Typography>
                                                <Typography variant="body2">{detail.provider_id_type || '—'}</Typography>
                                              </Grid>
                                              <Grid item xs={6} sm={4}>
                                                <Typography variant="caption" color="text.secondary">Reliance Level</Typography>
                                                <Typography variant="body2">{detail.reliance_level || '—'}</Typography>
                                              </Grid>
                                              <Grid item xs={6} sm={4}>
                                                <Typography variant="caption" color="text.secondary">Storage of Data</Typography>
                                                <Typography variant="body2">
                                                  {detail.storage_of_data === undefined ? '—' : detail.storage_of_data ? 'Yes' : 'No'}
                                                </Typography>
                                              </Grid>
                                              <Grid item xs={6} sm={4}>
                                                <Typography variant="caption" color="text.secondary">Data Sensitivity</Typography>
                                                <Typography variant="body2">{detail.data_sensitivity || '—'}</Typography>
                                              </Grid>
                                              <Grid item xs={6} sm={4}>
                                                <Typography variant="caption" color="text.secondary">Termination Reason</Typography>
                                                <Typography variant="body2">{detail.termination_reason || '—'}</Typography>
                                              </Grid>
                                              <Grid item xs={6} sm={4}>
                                                <Typography variant="caption" color="text.secondary">Notice Period (FE)</Typography>
                                                <Typography variant="body2">
                                                  {detail.notice_period_fe_days !== undefined ? `${detail.notice_period_fe_days} days` : '—'}
                                                </Typography>
                                              </Grid>
                                              <Grid item xs={6} sm={4}>
                                                <Typography variant="caption" color="text.secondary">Notice Period (Provider)</Typography>
                                                <Typography variant="body2">
                                                  {detail.notice_period_provider_days !== undefined ? `${detail.notice_period_provider_days} days` : '—'}
                                                </Typography>
                                              </Grid>
                                              <Grid item xs={6} sm={4}>
                                                <Typography variant="caption" color="text.secondary">Governing Law Country</Typography>
                                                <Typography variant="body2">{detail.governing_law_country || '—'}</Typography>
                                              </Grid>
                                              <Grid item xs={6} sm={4}>
                                                <Typography variant="caption" color="text.secondary">Service Provision Country</Typography>
                                                <Typography variant="body2">{detail.service_provision_country || '—'}</Typography>
                                              </Grid>
                                              <Grid item xs={6} sm={4}>
                                                <Typography variant="caption" color="text.secondary">Data Location (Rest)</Typography>
                                                <Typography variant="body2">{detail.data_location_rest || '—'}</Typography>
                                              </Grid>
                                              <Grid item xs={6} sm={4}>
                                                <Typography variant="caption" color="text.secondary">Data Location (Processing)</Typography>
                                                <Typography variant="body2">{detail.data_location_processing || '—'}</Typography>
                                              </Grid>
                                            </Grid>
                                          </Box>
                                        </Collapse>
                                      </TableCell>
                                    </TableRow>
                                  </React.Fragment>
                                );
                              })}
                              {arrDetails.length === 0 && (
                                <TableRow>
                                  <TableCell colSpan={8} align="center">
                                    No details found. Click "Add Detail" to create one.
                                  </TableCell>
                                </TableRow>
                              )}
                            </TableBody>
                          </Table>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              );
            })}
            {arrangements.length === 0 && (
              <TableRow>
                <TableCell colSpan={10} align="center">
                  {arrangementLoading ? 'Loading arrangements...' : 'No arrangements found'}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {detailError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {detailError}
        </Alert>
      )}

      {/* Arrangement dialog */}
      <Dialog open={arrangementDialogOpen} onClose={() => setArrangementDialogOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>{editingArrangement ? 'Edit Arrangement' : 'Add Arrangement'}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={4}>
              <TextField
                label="Reference"
                value={arrangementForm.reference_number}
                onChange={e => setArrangementForm(prev => ({ ...prev, reference_number: e.target.value }))}
                fullWidth
                required
                disabled={!!editingArrangement}
              />
            </Grid>
            <Grid item xs={12} md={4} sx={{ minWidth: 200 }}>
              <TextField
                label="Arrangement Type"
                select
                value={arrangementForm.arrangement_type}
                onChange={e => setArrangementForm(prev => ({ ...prev, arrangement_type: e.target.value }))}
                fullWidth
                required
              >
                {ARRANGEMENT_TYPES.map(type => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <Autocomplete
                value={arrangementForm.overarching_reference || null}
                onChange={(_, newValue) =>
                  setArrangementForm(prev => ({ ...prev, overarching_reference: newValue || '' }))
                }
                options={arrangements
                  .filter(a => a.id !== editingArrangement?.id && a.reference_number)
                  .map(a => a.reference_number)}
                freeSolo
                fullWidth
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Overarching Reference"
                    placeholder="Select or type..."
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6} sx={{ minWidth: 200 }}>
              <CurrencySelector
                value={arrangementForm.currency_code}
                onChange={code => setArrangementForm(prev => ({ ...prev, currency_code: code }))}
                label="Currency"
                required
                disabled={!canEdit}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Annual Cost"
                type="number"
                value={arrangementForm.annual_cost}
                onChange={e => setArrangementForm(prev => ({ ...prev, annual_cost: Number(e.target.value) }))}
                fullWidth
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setArrangementDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveArrangement} variant="contained" disabled={!canEdit || arrangementLoading}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Detail dialog */}
      <Dialog open={detailDialogOpen} onClose={() => setDetailDialogOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>{editingDetail ? 'Edit Detail' : 'Add Detail'}</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ py: 1 }}>
            {/* Basic Information */}
            <Typography variant="subtitle2" color="primary" gutterBottom sx={{ mt: 1, mb: 2 }}>
              Basic Information (B_04.01)
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Financial Entity LEI"
                  value={detailForm.financial_entity_lei}
                  onChange={e => setDetailForm(prev => ({ ...prev, financial_entity_lei: e.target.value.toUpperCase() }))}
                  fullWidth
                  required
                  inputProps={{ maxLength: 20 }}
                  error={detailForm.financial_entity_lei.length > 0 && detailForm.financial_entity_lei.length !== 20}
                  helperText={
                    detailForm.financial_entity_lei.length > 0 && detailForm.financial_entity_lei.length !== 20
                      ? `Must be exactly 20 characters (current: ${detailForm.financial_entity_lei.length})`
                      : "20-character Legal Entity Identifier (b_04.01.0020)"
                  }
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Entity Nature"
                  select
                  value={detailForm.entity_nature || ''}
                  onChange={e => setDetailForm(prev => ({ ...prev, entity_nature: e.target.value }))}
                  fullWidth
                  helperText="Nature of entity using the ICT service (b_04.01.0030)"
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {ENTITY_NATURE_OPTIONS.map(opt => (
                    <MenuItem key={opt} value={opt}>
                      {opt}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              {detailForm.entity_nature === 'Branch' && (
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Branch Identification Code"
                    value={detailForm.branch_identification_code || ''}
                    onChange={e => setDetailForm(prev => ({ ...prev, branch_identification_code: e.target.value }))}
                    fullWidth
                    required={detailForm.entity_nature === 'Branch'}
                    helperText="Required when Entity Nature is 'Branch' (b_04.01.0040)"
                  />
                </Grid>
              )}
              <Grid item xs={12} md={6}>
                <TextField
                  label="Function Identifier"
                  value={detailForm.function_identifier}
                  onChange={e => setDetailForm(prev => ({ ...prev, function_identifier: e.target.value }))}
                  fullWidth
                  required
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            {/* Provider Information */}
            <Typography variant="subtitle2" color="primary" gutterBottom sx={{ mb: 2 }}>
              Provider Information
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Provider ID Code"
                  value={detailForm.provider_id_code}
                  onChange={e => setDetailForm(prev => ({ ...prev, provider_id_code: e.target.value }))}
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Provider ID Type"
                  value={detailForm.provider_id_type}
                  onChange={e => setDetailForm(prev => ({ ...prev, provider_id_type: e.target.value }))}
                  fullWidth
                  required
                  helperText="e.g., LEI, National ID"
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            {/* Service Details */}
            <Typography variant="subtitle2" color="primary" gutterBottom sx={{ mb: 2 }}>
              Service Details
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sx={{ minWidth: 250 }}>
                <Autocomplete
                  options={ictServiceTypes}
                  getOptionLabel={(option) => `${option.code} - ${option.description.split(' - ')[0]}`}
                  value={ictServiceTypes.find(t => t.code === detailForm.ict_service_type) || null}
                  onChange={(_, newValue) => setDetailForm(prev => ({ ...prev, ict_service_type: newValue?.code || '' }))}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="ICT Service Type"
                      required
                    />
                  )}
                  disabled={!canEdit}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6} sx={{ minWidth: 160 }}>
                <TextField
                  label="Reliance Level"
                  select
                  value={detailForm.reliance_level || ''}
                  onChange={e => setDetailForm(prev => ({ ...prev, reliance_level: e.target.value }))}
                  fullWidth
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {RELIANCE_LEVELS.map(opt => (
                    <MenuItem key={opt} value={opt}>
                      {opt}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6} sx={{ minWidth: 150 }}>
                <TextField
                  label="Data Storage"
                  value={detailForm.storage_of_data === undefined || detailForm.storage_of_data === null ? '' : detailForm.storage_of_data ? 'true' : 'false'}
                  onChange={e =>
                    setDetailForm(prev => ({
                      ...prev,
                      storage_of_data: e.target.value === '' ? undefined : e.target.value === 'true',
                    }))
                  }
                  select
                  fullWidth
                >
                  <MenuItem value="">
                    <em>Unset</em>
                  </MenuItem>
                  <MenuItem value="true">Yes</MenuItem>
                  <MenuItem value="false">No</MenuItem>
                </TextField>
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            {/* Contract Dates & Terms */}
            <Typography variant="subtitle2" color="primary" gutterBottom sx={{ mb: 2 }}>
              Contract Dates & Terms
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Start Date"
                  type="date"
                  value={detailForm.start_date}
                  onChange={e => setDetailForm(prev => ({ ...prev, start_date: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="End Date"
                  type="date"
                  value={detailForm.end_date}
                  onChange={e => setDetailForm(prev => ({ ...prev, end_date: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  required
                  helperText="Use 9999-12-31 for indefinite"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Notice Period (Financial Entity)"
                  type="number"
                  value={detailForm.notice_period_fe_days ?? ''}
                  onChange={e =>
                    setDetailForm(prev => ({
                      ...prev,
                      notice_period_fe_days: e.target.value === '' ? undefined : Number(e.target.value),
                    }))
                  }
                  fullWidth
                  helperText="Days"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Notice Period (Provider)"
                  type="number"
                  value={detailForm.notice_period_provider_days ?? ''}
                  onChange={e =>
                    setDetailForm(prev => ({
                      ...prev,
                      notice_period_provider_days: e.target.value === '' ? undefined : Number(e.target.value),
                    }))
                  }
                  fullWidth
                  helperText="Days"
                />
              </Grid>
              {detailForm.end_date !== '9999-12-31' && (
                <Grid item xs={12}>
                  <TextField
                    label="Termination Reason"
                    value={detailForm.termination_reason || ''}
                    onChange={e => setDetailForm(prev => ({ ...prev, termination_reason: e.target.value }))}
                    fullWidth
                    multiline
                    rows={2}
                  />
                </Grid>
              )}
            </Grid>

            <Divider sx={{ my: 3 }} />

            {/* Data & Security */}
            <Typography variant="subtitle2" color="primary" gutterBottom sx={{ mb: 2 }}>
              Data & Security
            </Typography>
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <TextField
                  label="Data Sensitivity"
                  select
                  value={detailForm.data_sensitivity || ''}
                  onChange={e => setDetailForm(prev => ({ ...prev, data_sensitivity: e.target.value }))}
                  fullWidth
                  sx={{ minWidth: 150 }}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {DATA_SENSITIVITY.map(opt => (
                    <MenuItem key={opt} value={opt}>
                      {opt}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={6} sx={{ minWidth: 190 }}>
                <CountrySelector
                  value={detailForm.data_location_rest || ''}
                  onChange={code => setDetailForm(prev => ({ ...prev, data_location_rest: code }))}
                  label="Data Location (At Rest)"
                  disabled={!canEdit}
                />
              </Grid>
              <Grid item xs={12} md={6} sx={{ minWidth: 190 }}>
                <CountrySelector
                  value={detailForm.data_location_processing || ''}
                  onChange={code => setDetailForm(prev => ({ ...prev, data_location_processing: code }))}
                  label="Data Location (Processing)"
                  disabled={!canEdit}
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            {/* Legal & Jurisdictional */}
            <Typography variant="subtitle2" color="primary" gutterBottom sx={{ mb: 2 }}>
              Legal & Jurisdictional
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sx={{ minWidth: 215 }}>
                <CountrySelector
                  value={detailForm.governing_law_country || ''}
                  onChange={code => setDetailForm(prev => ({ ...prev, governing_law_country: code }))}
                  label="Governing Law Country"
                  disabled={!canEdit}
                />
              </Grid>
              <Grid item xs={12} sx={{ minWidth: 230 }}>
                <CountrySelector
                  value={detailForm.service_provision_country || ''}
                  onChange={code => setDetailForm(prev => ({ ...prev, service_provision_country: code }))}
                  label="Service Provision Country"
                  disabled={!canEdit}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveDetail} variant="contained" disabled={!canEdit || detailLoading}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ContractualArrangementsPanel;
